import cn from 'classnames'
import dynamic from 'next/dynamic'
import { useContext, type HTMLAttributes } from 'react'

import { PardotCookieAssociation } from '@components/pardot-association'
import { AllowedPageSection, TypePageFields, TypeVariationContainerDeviceType } from '../../lib/types'
import { isRussiaWarLP } from '../../lib/utils/page-rules'
import { PageBanner, PostNavBanner } from '../banner'
import { Breadcrumbs } from '../breadcrumbs'
import { PageFooter } from '../page-footer'
import { PageHead } from '../page-head'
import { PageHeader } from '../page-header'
import { useBackgroundStyle, getThemeFromBackgroundColor } from '../page-section/theme'
import { ContentRenderer, SectionRenderer } from '../renderer'
import { getSwooshStyle } from '../swoosh'
import { mergeSections } from './utils'
import { isTypeSectionGroup, isTypeVariationContainerDeviceType } from '../../lib/types/content-types'
import { SectionThemeContext } from '../../contexts/section-theme-context'
import { BackgroundColor } from '../../lib/types/background'
import { usePageTheme } from '../../contexts/page-theme-context'
import { useBrowserService, useExperimentService } from '../../lib/hooks'

import { MessengerWidget } from '../widget'

import styles from './page.module.scss'

interface CmsPageBodyProps extends HTMLAttributes<HTMLDivElement> {
  pageFields: TypePageFields
  showGoogleOneTap?: boolean
  reducedHeroPadding?: boolean
}

const DynamicGoogleOneTap = dynamic(() => import('../google-one-tap/default'))

// For brand refresh we want the footer background to match the bg color of section above it i.e the last section in the page.
const getLastSection = (sections: AllowedPageSection[], isMobileOrTablet: boolean): AllowedPageSection => {
  const lastSection = sections[sections.length - 1]
  // The Sections field in a page allows variation container device type, which contains a reference to the actual section.
  // In order to get the section of AllowedPageSection type from the variation container, we typecast the section after checking it's content type.
  if (isTypeVariationContainerDeviceType(lastSection)) {
    const lastSectionAsVC = lastSection as TypeVariationContainerDeviceType
    const lastSectionReference = isMobileOrTablet ? lastSectionAsVC.fields.mobile : lastSectionAsVC.fields.desktop
    // Variation Container Device Type accepts many references, we want to return the last section in the array of referenced sections.
    if (lastSectionReference) {
      return lastSectionReference[lastSectionReference.length - 1]
    }
  }
  return lastSection
}

export const CmsPageBody = (props: CmsPageBodyProps) => {
  const { pageFields, showGoogleOneTap: isEligibleGoogleOneTap, reducedHeroPadding, className, ...rest } = props
  const {
    pageId,
    isDiscountBannerEnabled,
    isDriftEnabled: isMessengerWidgetEnabled,
    seoMetadata,
    socialMetadata,
    header,
    showBreadcrumbs,
    heroSection: defaultHeroSection,
    sections: defaultSections,
    swoosh,
    pageTemplate,
    banner,
  } = pageFields

  const { className: swooshClassName, backgroundImage, cloudElements } = getSwooshStyle(swoosh)

  const heroSection = pageTemplate?.fields.pageContent.fields.heroSection ?? defaultHeroSection
  const sections = pageTemplate?.fields.pageContent.fields.sections ?? defaultSections
  // When the hero section is a sectionGroup with a BG color, we render the swoosh differently so that it can appear over the BG color.
  const isColoredHeroSectionGroupOnSwooshPage =
    isTypeSectionGroup(heroSection) &&
    heroSection?.fields.background?.color &&
    heroSection?.fields.background?.color !== BackgroundColor.Transparent

  const { navigation } = usePageTheme()
  const { isMobileOrTablet } = useBrowserService()
  const { isPaidLPWithGoogleOneTap } = useExperimentService()
  const sectionTheme = useContext(SectionThemeContext)
  const mergedSections = mergeSections(heroSection, sections)
  const [firstSection, ...otherSections] = mergedSections
  const firstSectionBackgroundStyle = useBackgroundStyle(sectionTheme, firstSection?.fields?.background?.color)
  const lastSectionBackgroundStyle = useBackgroundStyle(
    sectionTheme,
    sections ? getLastSection(sections, isMobileOrTablet).fields.background?.color : undefined
  )

  const showGoogleOneTap = () => {
    if (!isEligibleGoogleOneTap) {
      return false
    }
    if (pageFields.isGoogleOneTapEnabled) {
      return true
    }

    return isPaidLPWithGoogleOneTap()
  }

  const isFooter24Redesign = navigation === '2024'

  return (
    <>
      <PardotCookieAssociation />
      {showGoogleOneTap() && (
        <DynamicGoogleOneTap
          fixedPositionDesktop={{
            top: 64, // position GoogleOneTap prompt below the Ukraine Banner on the page
            right: 20,
          }}
        />
      )}
      {isMessengerWidgetEnabled && <MessengerWidget />}
      <div className={cn(styles.page, swooshClassName, className)} style={{ backgroundImage }} {...rest}>
        <PageHead seoMetadata={seoMetadata} socialMetadata={socialMetadata} />
        {cloudElements}
        <PageBanner isDiscountBannerEnabled={isDiscountBannerEnabled} />
        <PageHeader header={header} />
        <PostNavBanner />
        {banner && <ContentRenderer structure={banner} />}
        {showBreadcrumbs && (
          <div className={cn(styles.breadcrumbsContainer, ...firstSectionBackgroundStyle)}>
            <Breadcrumbs />
          </div>
        )}

        <main className={styles.main}>
          <SectionRenderer
            sectionGroupSwoosh={isColoredHeroSectionGroupOnSwooshPage ? swoosh : undefined}
            isHero
            isReducedHeroPadding={reducedHeroPadding}
            isH1Section
            structure={firstSection}
            startIndexInPage={0}
          />
          <SectionRenderer structure={otherSections} startIndexInPage={1} />
        </main>
        {!isRussiaWarLP(pageId) ? (
          <PageFooter
            className={cn(...(isFooter24Redesign ? lastSectionBackgroundStyle : []))}
            theme={
              sections &&
              getThemeFromBackgroundColor(getLastSection(sections, isMobileOrTablet).fields.background?.color)
            }
          />
        ) : null}
      </div>
    </>
  )
}
